import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { Mail, X, Sparkles, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function EmailDraftModal({ subRfqId, subRfqLabel, open, onClose }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState("");

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/sub-rfqs/${subRfqId}/draft-email`);
      setDraft(data);
    } catch { toast.error("Could not generate email"); }
    finally { setLoading(false); }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied");
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/70 z-40" />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="tray p-6 max-w-2xl w-full pointer-events-auto max-h-[90vh] overflow-y-auto" data-testid="email-modal">
              <div className="flex justify-between items-start mb-5">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-500" />
                  <div>
                    <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">Vendor Outreach</div>
                    <div className="font-display text-2xl">AI-Drafted Invitation Email</div>
                    <div className="text-xs text-text-secondary mt-0.5">{subRfqLabel}</div>
                  </div>
                </div>
                <button onClick={onClose} data-testid="email-close-btn" className="text-text-secondary hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!draft && !loading && (
                <div className="text-center py-10">
                  <Sparkles className="w-10 h-10 text-amber-500/60 mx-auto mb-3" />
                  <p className="text-sm text-text-secondary mb-5">
                    AI will draft a professional vendor invitation email with scope summary and signup link.
                  </p>
                  <button onClick={generate} data-testid="email-generate-btn" className="btn-tactile btn-amber inline-flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Generate Email
                  </button>
                </div>
              )}

              {loading && (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-3" />
                  <div className="font-mono-data text-xs uppercase tracking-widest text-text-secondary">Drafting...</div>
                </div>
              )}

              {draft && (
                <div className="space-y-4" data-testid="email-draft-output">
                  <div className="tray-inset p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary">Subject</div>
                      <button onClick={() => copy(draft.subject, "subject")} className="text-text-secondary hover:text-amber-500" data-testid="copy-subject-btn">
                        {copied === "subject" ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    <div className="font-display text-base">{draft.subject}</div>
                    {draft.preview_text && <div className="text-xs text-text-secondary mt-1 italic">{draft.preview_text}</div>}
                  </div>

                  <div className="tray-inset p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary">Body</div>
                      <button onClick={() => copy(draft.body, "body")} className="text-text-secondary hover:text-amber-500" data-testid="copy-body-btn">
                        {copied === "body" ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    <pre className="text-sm whitespace-pre-wrap font-ui leading-relaxed">{draft.body}</pre>
                  </div>

                  {draft.invite_link && (
                    <div className="tray-inset p-3 text-xs">
                      <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-1">Invite link</div>
                      <div className="break-all text-amber-500">{draft.invite_link}</div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button onClick={generate} disabled={loading} data-testid="email-regen-btn" className="btn-tactile flex-1 inline-flex items-center justify-center gap-2 text-xs">
                      <Sparkles className="w-3.5 h-3.5" /> Regenerate
                    </button>
                    <button onClick={() => copy(`Subject: ${draft.subject}\n\n${draft.body}`, "all")} data-testid="copy-all-btn" className="btn-tactile btn-amber flex-1 inline-flex items-center justify-center gap-2 text-xs">
                      <Copy className="w-3.5 h-3.5" /> Copy Full Email
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
